import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';

export default function RowTable({ file, rows, onClose }) {
  const columns = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const keySet = new Set();
    rows.forEach((r) => {
      if (r && typeof r === 'object') Object.keys(r).forEach((k) => keySet.add(k));
    });
    const cols = Array.from(keySet);
    return cols.filter((c) => c !== '_id');
  }, [rows]);
  const tableMinWidth = useMemo(() => {
    const perCol = 160;
    const base = 800;
    const max = 1600;
    return Math.min(max, Math.max(base, (columns.length || 1) * perCol));
  }, [columns.length]);

  const handleExport = () => {
    const sanitizedRows = Array.isArray(rows)
      ? rows.map((r) => {
          if (!r || typeof r !== 'object') return r;
          const copy = {};
          columns.forEach((c) => {
            copy[c] = r[c];
          });
          return copy;
        })
      : [];
    const ws = XLSX.utils.json_to_sheet(sanitizedRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Rows');
    const baseName = (file?.file_name || 'rows')
      .toString()
      // eslint-disable-next-line no-useless-escape
      .replace(/[^\w\-\.]+/g, '_')
      .replace(/_+/g, '_')
      .slice(0, 80);
    XLSX.writeFile(wb, `${baseName || 'rows'}.xlsx`);
  };

  return (
    <Paper elevation={3} sx={{ p: 2, mt: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            {file?.file_name || 'File rows'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {file?.workspace_name ? `${file.workspace_name}` : ''}
            {file?.total_rows ? ` • ${file.total_rows} rows` : ''}
          </Typography>
        </Box>
        <Box>
          <Button
            onClick={handleExport}
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            sx={{ mr: 1 }}
          >
            Export Excel
          </Button>
          <IconButton aria-label="Close table" onClick={onClose} color="primary">
            <CloseIcon />
          </IconButton>
        </Box>
      </Stack>
      <TableContainer sx={{ maxHeight: 520, overflowX: 'auto' }}>
        <Table
          stickyHeader
          size="small"
          sx={{ minWidth: tableMinWidth, width: 'max-content', tableLayout: 'auto' }}
        >
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col} sx={{ fontWeight: 600 }}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(rows) &&
              rows.map((r, idx) => (
                <TableRow key={idx} hover>
                  {columns.map((col) => {
                    const v = r?.[col];
                    let text = v;
                    if (v === null || v === undefined) text = '';
                    else if (typeof v === 'object') text = JSON.stringify(v);
                    return (
                      <TableCell
                        key={col}
                        sx={{
                          maxWidth: 320,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={String(text)}
                      >
                        {String(text)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
